import { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MainHeader from '../components/MainHeader';
import Main from '../components/Main';
import { Backdrop, Badge, Button, Chip, Container, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Popover, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TOBModuleOptions from '../components/auto_quotation/TOBModuleOptions';
import CustomTobPlanFormDialog from '../components/auto_quotation/CustomTobPlanFormDialog';
import SaveIcon from '@mui/icons-material/Save';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InputTable from '../components/InputTable';
import AutoQuotationHistory from '../components/auto_quotation/AutoQuotationHistory';
import { AuthContext } from '../utils/AuthContext';
import AlertDialog from '../components/AlertDialog';
import AddQuoteDialog from '../components/AddQuoteDialog';
import AddPlanDialog from '../components/AddPlanDialog';
import ManualPremiumFormDialog, { MANUAL_PREMIUM_PLAN_ID } from '../components/auto_quotation/ManualPremiumFormDialog';
import Lifeline_Compact_Rates_Dialog from '../components/auto_quotation/Lifeline_Compact_Rates_Dialog';
import BWHO_Compact_Rates_Dialog from '../components/auto_quotation/BWHO_Compact_Rates_Dialog';
import Company_Compact_Rates_Dialog from '../components/auto_quotation/Company_Compact_Rates_Dialog';
import OilGas_Compact_Rates_Dialog from '../components/auto_quotation/OilGas_Compact_Rates_Dialog';
import BHP_Indonesia_Compact_Rates_Dialog from '../components/auto_quotation/BHP_Indonesia_Compact_Rates_Dialog';
import ProMedico_Compact_Rates_Dialog from '../components/auto_quotation/ProMedico_Compact_Rates_Dialog';
import CignaCareConnect_Compact_Rates_Dialog from '../components/auto_quotation/CignaCareConnect_Compact_Rates_Dialog';
import IHHP_Compact_Rates_Dialog from '../components/auto_quotation/IHHP_Compact_Rates_Dialog';
import MorganPrice_Compact_Rates_Dialog from '../components/auto_quotation/MorganPrice_Compact_Rates_Dialog';
import OptimumGlobal_Compact_Rates_Dialog from '../components/auto_quotation/OptimumGlobal_Compact_Rates_Dialog';
import Vumi_Compact_Rates_Dialog from '../components/auto_quotation/Vumi_Compact_Rates_Dialog';
import MyHealthPhilippines_Compact_Rates_Dialog from '../components/auto_quotation/MyHealthPhilippines_Compact_Rates_Dialog';
import MyHealthSingapore_Compact_Rates_Dialog from '../components/auto_quotation/MyHealthSingapore_Compact_Rates_Dialog';
import MyHealthIndonesia_Compact_Rates_Dialog from '../components/auto_quotation/MyHealthIndonesia_Compact_Rates_Dialog';
import CignaGlobalHealthOption_Compact_Rates_Dialog from '../components/auto_quotation/CignaGlobalHealthOption_Compact_Rates_Dialog';
import PacificCrossPrimaryCarePlan_Compact_Rates_Dialog from '../components/auto_quotation/PacificCrossPrimaryCarePlan_Compact_Rates_Dialog';
import PacificCrossSelect_Compact_Rates_Dialog from '../components/auto_quotation/PacificCrossSelect_Compact_Rates_Dialog';
import PacificCrossBlueRoyale_Compact_Rates_Dialog from '../components/auto_quotation/PacificCrossBlueRoyale_Compact_Rates_Dialog';
import HSBC_SmartCareOptimumEnhanced_Compact_Rates_Dialog from '../components/auto_quotation/HSBC_SmartCareOptimumEnhanced_Compact_Rates_Dialog';
import QuotationPreviewTable, { normalizeAreaOfCoverDisplay } from '../components/auto_quotation/QuotationPreviewTable';
import TOBTable from '../components/auto_quotation/TOBTable';
import TOBTableMenuBar from '../components/auto_quotation/TOBTableMenuBar';
import ActionMenuBar from '../components/auto_quotation/ActionMenuBar';
import { patchCell, patchCellStyle, readCell, isBoldCell } from '../utils/tobTableEditing';
import AddTobDialog from '../components/AddTobDialog';
import EmailFormDialog, { wrapEmailHtml, EMAIL_STYLES, getLiveTableHTML } from '../components/EmailFormDialog';
import EmailDraftService from '../services/EmailDraftService';
import { TOB_MODULES, isUniversalTobId, normalizeTobId, setTobTableRowValue } from '../utils/tobPlanIds';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import SyncIcon from '@mui/icons-material/Sync';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import TranslateIcon from '@mui/icons-material/Translate';
import { DEFAULT_TOB_LANGUAGE, TOB_LANGUAGES, fetchTOBBenefits } from '../services/TOBService';
import { fetchAppConfig } from '../services/AppConfigService';
import { fetchInsurerRates } from '../services/InsurerRateService';
import { createAutoQuotationRecord, fetchAutoQuotationRecord, updateAutoQuotationRecord, downloadQuotationPdf, downloadQuotationExcel } from '../services/AutoQuotationService';
import { clearSharedClients, createEmptySharedClient, useSharedClients } from '../utils/sharedClients';
import { useSharedRatesVersion } from '../utils/sharedRatesVersion';
import { loadCachedInsurerRates, saveCachedInsurerRates } from '../utils/insurerRatesCache';
import { clearAllPageState, clearPageState, PAGE_KEYS, patchPageStateValues, usePersistedPageState } from '../utils/pageState';

// The section headers (Clients, Quotes, Plans, Quotation Preview, Table Of Benefits).
const SECTION_HEADER_SX = {
    fontSize: 18,
    mb: 1,
    borderLeft: '3px solid #3f51b5',
    pl: '7px',
    color: '#3f51b5',
    fontWeight: 700,
};

// What a Manual Premium plan added from the Quotation Preview is called until its insurer
// or plan is typed in.
const MANUAL_PREMIUM_DEFAULT_LABEL = 'Manual Premium';

// The Manual Premium dialog's copyFromFirstQuote fields, as the Quotation Preview names them.
const MANUAL_PREMIUM_COPIED_FIELDS = ['insurer', 'plan_label', 'area_of_coverage'];

// What a premium shares with the plan's premiums in other quotes and for other clients.
const PLAN_DETAIL_FIELDS = ['insurer', 'plan', 'plan_label', 'area_of_coverage', 'currency', 'payment_frequency'];

// Clients are shared with the Quotation page, so they can have been added or removed
// while this page was unmounted, leaving the restored quotations with the wrong columns.
function hasClientColumnsChanged(quotations, clients) {
    const clientUids = clients.map(client => client.uid).join('|');

    return (quotations || []).some(quotation =>
        (quotation.client_premiums || []).map(cp => cp.client_uid).join('|') !== clientUids
    );
}

// A plan's uid (when set) identifies this specific instance so plans sharing a tier's
// plan_id - e.g. two Silvers - can be told apart; plans created before per-instance uids
// existed fall back to plan_id, which was unique for them.
function getPlanKey(plan) {
    return plan.uid || plan.plan_id;
}

function SortablePlanChip({ plan, onEdit, onDelete, isStale }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: getPlanKey(plan) });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Badge
                badgeContent={
                    <Tooltip title="Open the plan dialog to fetch the missing premiums">
                        <PriorityHighIcon sx={{ fontSize: 13, color: '#fbc02d' }} />
                    </Tooltip>
                }
                invisible={!isStale}
                sx={{
                    '& .MuiBadge-badge': {
                        // A white circle keeps the "!" readable where it overlaps the purple chip.
                        backgroundColor: '#fff',
                        minWidth: 16,
                        width: 16,
                        height: 16,
                        padding: 0,
                        borderRadius: '50%',
                        // A red ring pulses out from the icon, matching the missing-premium icon in the preview table.
                        // Only the shadow animates: the badge's transform is what positions it on the chip's corner.
                        animation: 'stale-plan-pulse 1.4s ease-in-out infinite',
                    },
                    '@keyframes stale-plan-pulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.55)' },
                        '50%': { boxShadow: '0 0 0 5px rgba(244, 67, 54, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
                    },
                }}
            >
                <Chip size="small"
                    label={plan.label}
                    onClick={onEdit}
                    onDelete={onDelete}
                    sx={{ backgroundColor: '#7e57c2', color: '#fff', cursor: 'inherit', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }}
                />
            </Badge>
        </div>
    );
}

function SortableTobChip({ plan, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: plan.uid });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Chip size="small"
                label={plan.label}
                onDelete={onDelete}
                sx={{ backgroundColor: '#7e57c2', color: '#fff', cursor: 'inherit', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }}
            />
        </div>
    );
}

function AutoQuotation() {
    const [open, setOpen] = useState(localStorage.getItem('quoting-drawer-open') == 'true');

    const BUTTONS_MIN_WIDTH = 145;

    const content = localStorage.getItem('quoting-app-config');
    const configTimestamp = localStorage.getItem('quoting-app-config-timestamp');
    const [appConfig, setAppConfig] = useState({
        content: JSON.parse(content),
        configTimestamp
    });

    // Seeded from localStorage so the Rates Version selectors have something to show
    // immediately, instead of sitting empty until handleFetchInsurerRates resolves.
    const [insurerRates, setInsurerRates] = useState(() => loadCachedInsurerRates());
    const [fetchingInsurerRates, setFetchingInsurerRates] = useState(false);

    const currencies = appConfig.content?.currencies || [];
    const area_of_coverages = appConfig.content?.area_of_coverages || [];
    const relationships = appConfig.content?.relationships || [];

    const refInputTable = useRef();
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const { id: autoQuotationId } = useParams();

    // Everything declared with page.useState / page.useRef below is kept in localStorage,
    // so leaving this page and coming back resumes the same quotation.
    const page = usePersistedPageState(PAGE_KEYS.autoQuotation, { contextId: autoQuotationId ?? null });

    page.useScrollRestoration();

    const [countryOfResidence, setCountryOfResidence] = page.useState('countryOfResidence', '');
    const [nationality, setNationality] = page.useState('nationality', '');

    // [REQUIRED FOR NEW INSURER] one entry per Compact_Rates_Dialog insurer. Shared with
    // the Quotation page (see useSharedRatesVersion), so a version picked on either page
    // carries over to the other.
    const [selectedRatesVersion, setSelectedRatesVersion] = useSharedRatesVersion({
        lifeline: '',
        bwho: '',
        company: '',
        oil_gas: '',
        bhp_indonesia: '',
        pro_medico: '',
        cigna_care_connect: '',
        ihhp: '',
        morgan_price: '',
        optimum_global: '',
        vumi: '',
        my_health_philippines: '',
        my_health_singapore: '',
        my_health_indonesia: '',
        cigna_global_health_option: '',
        pacific_cross_primary_care_plan: '',
        pacific_cross_select: '',
        pacific_cross_blue_royale: '',
        hsbc_smart_care_optimum_enhanced: '',
    });

    const [showRefreshNow, setShowRefreshNow] = useState(false);
    const [clientsUpdated, setClientsUpdated] = useState(false);
    const prevClientsRef = useRef(null);

    const [loadingAutoQuotationRecord, setLoadingAutoQuotationRecord] = useState(false);
    const [showRemoveClient, setShowRemoveClient] = useState(false);
    const [removeClientIndex, setRemoveClientIndex] = useState();

    const [addCustomTobPlanOpen, setAddCustomTobPlanOpen] = useState(false);
    const [selectedTobModules, setSelectedTobModules] = page.useState('selectedTobModules', () => TOB_MODULES.map(module => module.name));
    const initialTableStateRef = page.useRef('initialTableState', () => ({
        quotations: [],
        tobPlans: [],
        selectedTobModules: TOB_MODULES.map(module => module.name),
    }));
    const initialTableStateReadyRef = useRef(false);

    function isDeepEqual(valueA, valueB) {
        return JSON.stringify(valueA) === JSON.stringify(valueB);
    }

    const visibleTobRowNumbers = [1, 2, 3, 4, 5].map(String).concat(
        TOB_MODULES.filter(module => selectedTobModules.includes(module.name)).flatMap(module =>
            Array.from({ length: module.end - module.start + 1 }, (_, index) => String(module.start + index))
        )
    );

    function handleToggleTobModule(moduleName) {
        setSelectedTobModules(prev => {
            if (prev.includes(moduleName)) {
                return prev.filter(name => name !== moduleName);
            }
            return [...prev, moduleName];
        });
    }

    function handleOpenAddCustomTobPlan() {
        setAddCustomTobPlanOpen(true);
        loadTobBenefitLabels();
    }

    function handleCloseAddCustomTobPlan() {
        setAddCustomTobPlanOpen(false);
    }

    function createClient(clientsLength = 0) {
        const uid = uuidv4();

        return {
            uid,
            full_name: '',
            age: '',
            gender: '',
            nationality: nationality,
            country_of_residence: countryOfResidence,
            relationship: clientsLength > 0 ? 'Dependent' : '',
            payment_frequency: '',
            currency: '',
            area_of_coverage: '',
        };
    }

    function isNewQuotationClearable() {
        return Boolean(autoQuotationId);
    }

    function resetAutoQuotation() {
        const initialClients = [createEmptySharedClient()];
        const initialQuotes = [createQuote('Quote 1: Nil Deductible')];
        const initialQuotations = buildQuotations(initialQuotes, initialClients, [], []);

        // A new quotation starts from a clean sheet on both pages, not just this one:
        // page.reset() puts every saved value of this page back to its default, and the
        // Quotation page's own snapshot and the shared clients go with it.
        page.reset();
        clearAllPageState();
        clearSharedClients();

        setClients(initialClients);
        prevClientsRef.current = JSON.parse(JSON.stringify(initialClients));
        setQuotes(initialQuotes);
        setQuotations(initialQuotations);
        setQuoteHistoryAnchor(null);
        setSaveConfirmOpen(false);
        setNewQuotationConfirmOpen(false);
        setAddQuoteOpen(false);
        setQuoteToEdit(null);
        setQuoteToDelete(null);
        setDeleteQuoteOpen(false);
        setAddPlanOpen(false);
        setLifelineCompactOpen(false);
        setBwhoCompactOpen(false);
        setCompanyCompactOpen(false);
        setOilGasCompactOpen(false);
        setBHPIndonesiaCompactOpen(false);
        setProMedicoCompactOpen(false);
        setCignaCareConnectCompactOpen(false);
        setIhhpCompactOpen(false);
        setMorganPriceCompactOpen(false);
        setOptimumGlobalCompactOpen(false);
        setVumiCompactOpen(false);
        setMyHealthPhilippinesCompactOpen(false);
        setMyHealthSingaporeCompactOpen(false);
        setMyHealthIndonesiaCompactOpen(false);
        setCignaGlobalHealthOptionCompactOpen(false);
        setPacificCrossPrimaryCarePlanCompactOpen(false);
        setPacificCrossSelectCompactOpen(false);
        setPacificCrossBlueRoyaleCompactOpen(false);
        setHSBCSmartCareOptimumEnhancedCompactOpen(false);
        setEditingPlan(null);
        setEmailDialogKey(uuidv4());
        initialTableStateRef.current = {
            quotations: initialQuotations,
            tobPlans: [],
            selectedTobModules: TOB_MODULES.map(module => module.name),
            tobLanguage: DEFAULT_TOB_LANGUAGE,
        };
        initialTableStateReadyRef.current = true;
        setShowRemoveClient(false);
        setRemoveClientIndex(undefined);
    }

    function requestNewQuotation() {
        if (!needToSave) {
            resetAutoQuotation();
            navigate('/auto_quotation');
            return;
        }

        setNewQuotationConfirmOpen(true);
    }

    function confirmNewQuotation() {
        resetAutoQuotation();
        navigate('/auto_quotation');
    }

    // Shared with the Quotation page through localStorage
    const [clients, setClients, handleClientsInput] = useSharedClients(() => [createClient()], refInputTable);

    function handleAddClient() {
        const newClient = createClient(clients.length);
        const nextClients = [...clients, newClient];
        setClients(nextClients);
        setQuotations(buildQuotations(quotes, nextClients, plans, quotations));
        markAllPlansStale();
    }

    async function saveAutoQuotationRecord({ showToast = true, shouldNavigate = true, additionalPayload = {} } = {}) {
        if (!refInputTable.current?.validateForm()) {
            if (showToast) {
                toast.error('Please fill in all required client fields.', { hideProgressBar: true });
            }
            return null;
        }

        // Delay the actual save slightly to allow AlertDialog to finish closing
        await new Promise(resolve => setTimeout(resolve, 300));

        setSavingAutoQuotationRecord(true);

        try {
            const payload = {
                clients,
                quotes,
                plans,
                quotations,
                tob_benefit_labels_column: tobBenefitLabelsColumn,
                tob_plans: tobPlans,
                selected_tob_modules: selectedTobModules,
                language: tobLanguage,
                ...additionalPayload,
            };

            const recordId = selectedQuotation?.id;
            const response = recordId
                ? await updateAutoQuotationRecord({ recordId, body: payload, token: auth.token })
                : await createAutoQuotationRecord({ body: payload, token: auth.token });

            const record = response?.data?.auto_quotation_record;
            if (record?.id) {
                setSelectedQuotation(record);
                setNeedToSave(false);

                // The page already holds exactly what was just saved, so moving to the
                // record's own URL below must not fetch it back over the top.
                loadedRecordIdRef.current = String(record.id);
                initialTableStateRef.current = {
                    quotations,
                    tobPlans,
                    selectedTobModules,
                    tobLanguage,
                };
                initialTableStateReadyRef.current = true;

                if (showToast) {
                    toast.success('Auto quotation saved.', {
                        autoClose: 2500,
                        hideProgressBar: true,
                    });
                }

                if (shouldNavigate) {
                    navigate(`/auto_quotation/${record.id}`);
                }

                return record;
            }
        } catch (error) {
            console.error('Failed to save auto quotation record:', error);
            if (showToast) {
                toast.error('An Error Has Occurred.', {
                    autoClose: 2500,
                    hideProgressBar: true,
                });
            }
        } finally {
            setSavingAutoQuotationRecord(false);
        }
        return null;
    }

    async function handleSaveAutoQuotationRecord() {
        setSaveConfirmOpen(false);
        await saveAutoQuotationRecord();
    }

    async function loadAutoQuotationRecord(recordId) {
        setLoadingAutoQuotationRecord(true);
        try {
            const response = await fetchAutoQuotationRecord({ recordId, token: auth.token });
            const record = response?.data?.auto_quotation_record;
            if (!record) {
                throw new Error('Auto quotation record not found');
            }

            const nextClients = record.clients ?? [];
            const nextQuotes = record.quotes ?? [];
            const nextPlans = record.plans ?? [];
            const nextTobBenefitLabelsColumn = record.tob_benefit_labels_column ?? undefined;
            const nextTobPlans = record.tob_plans ?? [];
            const nextSelectedTobModules = record.selected_tob_modules ?? [];
            const nextQuotations = record.quotations ?? buildQuotations(nextQuotes, nextClients, nextPlans, nextQuotes);

            setSelectedQuotation(record);
            setClients(nextClients);
            setQuotes(nextQuotes);
            setPlans(nextPlans);
            setTobBenefitLabelsColumn(nextTobBenefitLabelsColumn);
            setTobPlans(nextTobPlans);
            setSelectedTobModules(nextSelectedTobModules);
            setTobLanguage(record.language || DEFAULT_TOB_LANGUAGE);
            setQuotations(nextQuotations);
            setNeedToSave(false);
            setEmailDialogKey(recordId);
            initialTableStateRef.current = {
                quotations: nextQuotations,
                tobPlans: nextTobPlans,
                selectedTobModules: nextSelectedTobModules,
                tobLanguage: record.language || DEFAULT_TOB_LANGUAGE,
            };
            initialTableStateReadyRef.current = true;

            // Whatever the Quotation page was holding belongs to a different quotation
            // than the record just opened here, so it starts over - on this record's
            // clients, which are shared and were set above.
            clearPageState(PAGE_KEYS.quotation);
        } catch (error) {
            console.error('Failed to load auto quotation record:', error);
            toast.error('Unable to load quotation record.', {
                autoClose: 2500,
                hideProgressBar: true,
            });
        } finally {
            setLoadingAutoQuotationRecord(false);
        }
    }

    async function handleFetchAppConfig() {
        const res = await fetchAppConfig({ token: auth.token, rates_version_per_insurer: selectedRatesVersion });

        const config = res.data;

        localStorage.setItem('quoting-app-config-timestamp', config.timestamp);
        localStorage.setItem('quoting-app-config', config.content);

        setAppConfig({
            content: JSON.parse(config.content),
            configTimestamp: config.timestamp
        });
    }

    async function handleFetchInsurerRates() {
        setFetchingInsurerRates(true);

        const response = await fetchInsurerRates({ token: auth.token });

        setInsurerRates(response.data.insurer_rates);
        saveCachedInsurerRates(response.data.insurer_rates);

        setFetchingInsurerRates(false);
    }

    // Rates versions are shared with the Quotation page (see useSharedRatesVersion), so a
    // version picked here means that page's premiums were computed against an outdated
    // version - even while it isn't open to react to it itself.
    function handleRatesVersionChanged() {
        patchPageStateValues(PAGE_KEYS.quotation, { showRefreshNow: true });
    }

    function handleRemoveClient(index) {
        const nextClients = JSON.parse(JSON.stringify(clients));
        nextClients.splice(index, 1);
        setClients(nextClients);
        setQuotations(buildQuotations(quotes, nextClients, plans, quotations));
        markAllPlansStale();
    }

    function handleAutoQuotationRecordDelete(record) {
        setQuoteHistoryAnchor(null);
        if (selectedQuotation?.id === record.id) {
            resetAutoQuotation();
            navigate('/auto_quotation');
        }
    }

    const [tobBenefitLabelsColumn, setTobBenefitLabelsColumn] = page.useState('tobBenefitLabelsColumn', undefined);
    const [tobPlans, setTobPlans] = page.useState('tobPlans', []);
    const [addTobOpen, setAddTobOpen] = useState(false);
    const [tobPlaceholderToFill, setTobPlaceholderToFill] = useState(null);
    const [tobPlanToDelete, setTobPlanToDelete] = useState(null);
    const [deleteTobPlanOpen, setDeleteTobPlanOpen] = useState(false);

    // TOB fetches nest and overlap (handleAddTob loads the benefit labels before its own
    // plan, and plans can be added back to back), so a count is what tells us whether any
    // request is still in flight -- a boolean would be cleared by the first one to finish.
    const [pendingTobFetches, setPendingTobFetches] = useState(0);
    const loadingTobBenefits = pendingTobFetches > 0;

    // Which language's TOB spreadsheet the TOB table is loaded from.
    const [tobLanguage, setTobLanguage] = page.useState('tobLanguage', DEFAULT_TOB_LANGUAGE);
    const [tobLanguageMenuAnchor, setTobLanguageMenuAnchor] = useState(null);
    const [tobLanguageToConfirm, setTobLanguageToConfirm] = useState(null);
    const [changingTobLanguage, setChangingTobLanguage] = useState(false);

    async function loadTobBenefitLabels() {
        if (tobBenefitLabelsColumn) return;

        setPendingTobFetches(count => count + 1);
        try {
            const response = await fetchTOBBenefits({ planIds: ['benefit_labels'], language: tobLanguage, token: auth.token });
            if (response?.data?.table) {
                setTobBenefitLabelsColumn(response.data.table);
            }
        } catch (error) {
            console.error('Failed to load TOB benefit labels:', error);
        } finally {
            setPendingTobFetches(count => count - 1);
        }
    }

    function handleOpenAddTobDialog() {
        setTobPlaceholderToFill(null);
        setAddTobOpen(true);
        loadTobBenefitLabels();
    }

    // The placeholder's Add button opens the same dialog, but the TOB picked there takes
    // over the placeholder's uid so it replaces it in place, still linked to its plan.
    function handleOpenFillTobPlaceholder(placeholder) {
        setTobPlaceholderToFill(placeholder);
        setAddTobOpen(true);
        loadTobBenefitLabels();
    }

    function handleCloseAddTobDialog() {
        setAddTobOpen(false);
        setTobPlaceholderToFill(null);
    }

    function handleSubmitAddTob(tobPlan) {
        if (!tobPlaceholderToFill) {
            handleAddTob(tobPlan);
            return;
        }
        // Carries the plan's Currency / Area of Coverage over, the way addCorrespondingTob does.
        const linkedPlan = plans.find(p => getPlanKey(p) === tobPlaceholderToFill.uid);
        handleAddTob(
            { ...tobPlan, uid: tobPlaceholderToFill.uid },
            null,
            linkedPlan?.payloads?.currency,
            linkedPlan?.payloads?.area_of_coverage,
        );
    }

    // Stands in for a plan that has no TOB, so the table still shows a column for it.
    function createTobPlaceholder(plan) {
        return { id: plan.id, label: plan.label, uid: plan.uid || uuidv4(), placeholder: true };
    }

    // Replaces the TOB already holding this uid (a placeholder being filled) in place,
    // otherwise appends it.
    function upsertTobPlan(tobPlan) {
        setTobPlans(prev => (
            prev.some(p => p.uid === tobPlan.uid)
                ? prev.map(p => (p.uid === tobPlan.uid ? tobPlan : p))
                : [...prev, tobPlan]
        ));
    }

    function handleRequestDeleteTobPlan(plan) {
        setTobPlanToDelete(plan);
        setDeleteTobPlanOpen(true);
    }

    function handleDeleteTobPlan(plan) {
        setTobPlans(prev => {
            if (plan?.uid) {
                return prev.filter(p => p.uid !== plan.uid);
            }
            const firstMatchIndex = prev.findIndex(p => p.id === plan?.id);
            if (firstMatchIndex === -1) return prev;
            return prev.filter((_, idx) => idx !== firstMatchIndex);
        });
        setTobSelection(prev => (prev?.planUid === plan?.uid ? null : prev));
    }

    function confirmDeleteTobPlan() {
        if (!tobPlanToDelete) {
            setDeleteTobPlanOpen(false);
            return;
        }
        handleDeleteTobPlan(tobPlanToDelete);
        setTobPlanToDelete(null);
        setDeleteTobPlanOpen(false);
    }

    async function handleAddTob(plan, fetchId = null, currency = null, areaOfCoverage = null) {
        // A TOB added for a quoted plan arrives with that plan's key as its uid, so the TOB
        // column, the plan and its premiums (plan_uid) all share one id; a TOB added on its
        // own from the Add TOB dialog belongs to no plan and gets a fresh one.
        const nextPlan = { ...plan, uid: plan.uid || uuidv4() };
        const idToFetch = fetchId || plan.id;

        // Held across the labels load too, so the count never dips to zero between the two
        // requests and briefly unblocks navigation.
        setPendingTobFetches(count => count + 1);
        try {
            await loadTobBenefitLabels();

            // A custom TOB plan already carries its own table/vertical_merge_cells (fetched
            // when it was selected), so it must not go through the universal_output lookup,
            // which wouldn't recognize its plan id and would wipe the table out.
            if (!nextPlan.table) {
                try {
                    const response = await fetchTOBBenefits({ planIds: [idToFetch], language: tobLanguage, token: auth.token });
                    if (response?.data?.table) {
                        nextPlan.table = response.data.table;
                        nextPlan.vertical_merge_cells = response.data.vertical_merge_cells;
                    }
                } catch (error) {
                    console.error('Failed to load TOB benefits:', error);
                    toast.error('Unable to load TOB table data for this plan.');
                }
            }

            if (!nextPlan.table) {
                upsertTobPlan(createTobPlaceholder(nextPlan));
                return;
            }

            if (currency) nextPlan.table = setTobTableRowValue(nextPlan.table, 4, currency);
            if (areaOfCoverage) nextPlan.table = setTobTableRowValue(nextPlan.table, 5, normalizeAreaOfCoverDisplay(areaOfCoverage, tobLanguage));

            delete nextPlan.placeholder;
            upsertTobPlan(nextPlan);
        } finally {
            setPendingTobFetches(count => count - 1);
        }
    }

    // The universal_output column a TOB was loaded from, or null for TOBs that don't come from
    // it (placeholders, custom TOB plans). A plan's TOB is fetched by its normalized plan id
    // (see addCorrespondingTob); a TOB added from the Add TOB dialog by its own id.
    function getTobFetchId(tob) {
        if (tob.placeholder) return null;
        const linkedPlan = plans.find(p => getPlanKey(p) === tob.uid) || plans.find(p => p.plan_id === tob.id);
        const linkedPlanTobId = linkedPlan && normalizeTobId(linkedPlan.plan_id, linkedPlan.payloads);
        if (isUniversalTobId(linkedPlanTobId) && linkedPlan.plan_id === tob.id) return linkedPlanTobId;
        const ownTobId = tob.id && normalizeTobId(tob.id);
        return isUniversalTobId(ownTobId) ? ownTobId : null;
    }

    function requestTobLanguageChange(language) {
        setTobLanguageMenuAnchor(null);
        if (language === tobLanguage) return;
        // Reloading replaces the TOB columns, edits included, so that needs a confirmation.
        if (tobPlans.some(getTobFetchId)) {
            setTobLanguageToConfirm(language);
            return;
        }
        changeTobLanguage(language);
    }

    // Switches the TOB language and reloads the benefit labels and every TOB column loaded from
    // universal_output in it, so the table never mixes languages. Custom TOB plans and
    // placeholders are left as they are.
    async function changeTobLanguage(language) {
        setTobLanguage(language);

        const reloads = tobPlans
            .map(tob => {
                const linkedPlan = plans.find(p => getPlanKey(p) === tob.uid);
                return {
                    uid: tob.uid,
                    fetchId: getTobFetchId(tob),
                    currency: linkedPlan?.payloads?.currency,
                    areaOfCoverage: linkedPlan?.payloads?.area_of_coverage,
                };
            })
            .filter(reload => reload.fetchId);
        if (!tobBenefitLabelsColumn && reloads.length === 0) return;

        const fetchTable = planId => fetchTOBBenefits({ planIds: [planId], language, token: auth.token })
            .then(response => response?.data?.table ? response.data : null)
            .catch(error => {
                console.error('Failed to reload TOB benefits:', error);
                return null;
            });

        setPendingTobFetches(count => count + 1);
        setChangingTobLanguage(true);
        try {
            const [labels, ...tables] = await Promise.all([
                tobBenefitLabelsColumn ? fetchTable('benefit_labels') : null,
                ...reloads.map(reload => fetchTable(reload.fetchId)),
            ]);

            if (labels) setTobBenefitLabelsColumn(labels.table);

            const reloadedByUid = new Map();
            reloads.forEach((reload, index) => {
                const data = tables[index];
                if (!data) return;
                let table = data.table;
                if (reload.currency) table = setTobTableRowValue(table, 4, reload.currency);
                if (reload.areaOfCoverage) table = setTobTableRowValue(table, 5, normalizeAreaOfCoverDisplay(reload.areaOfCoverage, language));
                reloadedByUid.set(reload.uid, { table, vertical_merge_cells: data.vertical_merge_cells });
            });
            setTobPlans(prev => prev.map(tob => (reloadedByUid.has(tob.uid) ? { ...tob, ...reloadedByUid.get(tob.uid) } : tob)));
            setTobSelection(null);

            if ((tobBenefitLabelsColumn && !labels) || reloadedByUid.size < reloads.length) {
                toast.error(`Some TOBs could not be loaded in ${language}.`, { hideProgressBar: true });
            }
        } finally {
            setPendingTobFetches(count => count - 1);
            setChangingTobLanguage(false);
        }
    }

    function createQuote(name) {
        return { uid: uuidv4(), name };
    }

    const [quoteHistoryAnchor, setQuoteHistoryAnchor] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = page.useState('selectedQuotation', null);

    // The snapshot belongs to the record that is open, which is not the URL id after a
    // reset: a blank page must never be restored on top of a saved record.
    page.setContextId(selectedQuotation?.id ?? null);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [newQuotationConfirmOpen, setNewQuotationConfirmOpen] = useState(false);
    const [savingAutoQuotationRecord, setSavingAutoQuotationRecord] = useState(false);
    const [creatingEmailDraft, setCreatingEmailDraft] = useState(false);
    const [downloadPdfConfirmOpen, setDownloadPdfConfirmOpen] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadExcelConfirmOpen, setDownloadExcelConfirmOpen] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailDialogKey, setEmailDialogKey] = useState(uuidv4());
    const [quotations, setQuotations] = page.useState('quotations', []);
    const [needToSave, setNeedToSave] = page.useState('needToSave', false);
    const [stalePlanIds, setStalePlanIds] = page.useState('stalePlanIds', () => new Set(), {
        serialize: value => Array.from(value),
        deserialize: value => new Set(value)
    });

    useEffect(() => {
        prevClientsRef.current = JSON.parse(JSON.stringify(clients));

        // A restored page keeps its quotations as they were, including the baseline of
        // what "saved" looks like, and is only rebuilt when the shared clients changed
        // while it was unmounted. Rebuilding keeps one premium column per current client
        // and preserves what was already filled in for the clients that are still there.
        if (page.restored && !hasClientColumnsChanged(quotations, clients)) {
            initialTableStateReadyRef.current = true;
            return;
        }

        const initialQuotations = buildQuotations(quotes, clients, plans, quotations);
        setQuotations(initialQuotations);

        if (!page.restored) {
            initialTableStateRef.current = {
                quotations: initialQuotations,
                tobPlans,
                selectedTobModules,
                tobLanguage,
            };
        }

        initialTableStateReadyRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A restored page state already holds its record, including edits that were never
    // saved to the server, so that record counts as loaded and must not be fetched over.
    const loadedRecordIdRef = useRef(
        page.restored && selectedQuotation?.id != null ? String(selectedQuotation.id) : null
    );

    useEffect(() => {
        if (!autoQuotationId || !auth?.token) return;
        if (loadedRecordIdRef.current === autoQuotationId) return;

        loadedRecordIdRef.current = autoQuotationId;
        loadAutoQuotationRecord(autoQuotationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoQuotationId, auth?.token]);

    // Coming back to /auto_quotation restores the record that was open, so put its id
    // back in the URL - a reload, the history list and Download/Email all read it there.
    useEffect(() => {
        if (!page.restored || autoQuotationId || selectedQuotation?.id == null) return;

        navigate(`/auto_quotation/${selectedQuotation.id}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refreshes the cached app config / insurer rates on mount, same as the Quotation page.
    useEffect(() => {
        handleFetchAppConfig();
        handleFetchInsurerRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        console.log('[AutoQuotation] quotations state updated:', JSON.parse(JSON.stringify(quotations)));
    }, [quotations]);

    // Keeps every TOB plan's Plan Type (row 2) in sync with the plan_label of its
    // matching premium in quotations, so edits like changing optional covers are reflected.
    useEffect(() => {
        setTobPlans(prev => {
            let changed = false;

            const next = prev.map(tobPlan => {
                // A placeholder has no table to write into; giving it one would turn it into a blank TOB.
                if (tobPlan.placeholder) return tobPlan;
                let planLabel = null;

                for (const quotation of quotations) {
                    for (const cp of quotation.client_premiums || []) {
                        // The TOB's uid is its plan's key, which premiums carry as plan_uid (or
                        // plan_id for uid-less plans); older TOBs fall back to the tier match.
                        const premiums = cp.premiums || [];
                        const premium = premiums.find(p => (p.plan_uid || p.plan_id) === tobPlan.uid)
                            || premiums.find(p => p.plan_id === tobPlan.id);
                        if (premium?.plan_label) {
                            planLabel = premium.plan_label;
                            break;
                        }
                    }
                    if (planLabel) break;
                }

                if (!planLabel) return tobPlan;

                const currentValue = tobPlan.table?.['2']?.['1']?.cell_value;
                if (currentValue === planLabel) return tobPlan;

                changed = true;
                return { ...tobPlan, table: setTobTableRowValue(tobPlan.table, 2, planLabel) };
            });

            return changed ? next : prev;
        });
        // tobPlans is intentionally a dependency: a TOB plan is added asynchronously
        // (handleAddTob awaits fetchTOBBenefits) after quotations already updated with the
        // new premium, so this also has to re-run once that plan actually lands in tobPlans.
        // Safe from loops: once row 2 already matches plan_label, the updater returns the
        // same `prev` reference and bails out.
    }, [quotations, tobPlans]);

    useEffect(() => {
        if (showRefreshNow) {
            markAllPlansStale();
            setShowRefreshNow(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRefreshNow]);

    useEffect(() => {
        if (!initialTableStateReadyRef.current) return;

        const currentState = {
            quotations,
            tobPlans,
            selectedTobModules,
            tobLanguage,
        };

        const initialState = initialTableStateRef.current;
        setNeedToSave(!isDeepEqual(currentState, initialState));
    }, [quotations, tobPlans, selectedTobModules, tobLanguage]);


    useEffect(() => {
        if (!clientsUpdated || !isShowingResult) {
            setClientsUpdated(false);
            return;
        }
        const prev = prevClientsRef.current;
        const current = refInputTable.current?.getClientData() ?? clients;
        const fieldsToCheck = ['full_name', 'age', 'gender', 'nationality', 'country_of_residence', 'relationship'];
        let changed = false;
        if (!prev || prev.length !== current.length) {
            changed = true;
        } else {
            for (let i = 0; i < current.length; i++) {
                for (const field of fieldsToCheck) {
                    if ((prev[i]?.[field] ?? '') !== (current[i]?.[field] ?? '')) {
                        changed = true;
                        break;
                    }
                }
                if (changed) break;
            }
        }
        if (changed) markAllPlansStale();
        prevClientsRef.current = JSON.parse(JSON.stringify(current));
        setClientsUpdated(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientsUpdated]);

    function buildQuotations(nextQuotes, nextClients, nextPlans, existingQuotations = []) {
        // A quote or client added after a plan was quoted has no premium for it yet. Without
        // one the plan chip's label -- insurer and plan joined -- would land in Plan Type with
        // no insurer, so the plan's details are taken from its premium elsewhere, preferring
        // the same quote. Deductible and rate stay empty: they differ per quote and client.
        const findPlanDetails = (plan, sameQuotation) => {
            const matchesPlan = p => (plan.uid ? p.plan_uid === plan.uid : p.plan_id === plan.plan_id);
            const quotationsToSearch = [sameQuotation, ...existingQuotations].filter(Boolean);
            for (const quotation of quotationsToSearch) {
                for (const cp of quotation.client_premiums || []) {
                    const premium = cp.premiums?.find(p => matchesPlan(p) && !p.blank);
                    if (premium) {
                        // Left out when missing: the result is spread over the defaults below.
                        return Object.fromEntries(PLAN_DETAIL_FIELDS
                            .filter(field => premium[field] != null)
                            .map(field => [field, premium[field]]));
                    }
                }
            }
            return {};
        };

        const result = nextQuotes.map(quote => {
            const existingQuotation = existingQuotations.find(q => q.uid === quote.uid);
            return {
                uid: quote.uid,
                name: quote.name,
                client_premiums: nextClients.map(client => {
                    const existingClientPremium = existingQuotation?.client_premiums?.find(cp => cp.client_uid === client.uid);
                    return {
                        client_uid: client.uid,
                        premiums: nextPlans.map(plan => {
                            // Match on plan_uid when the plan carries one, since plan_id alone can't
                            // tell apart two instances of the same tier (e.g. two Silvers); plans
                            // without a uid (pre-dating per-instance ids) still match by plan_id.
                            const existingPremium = existingClientPremium?.premiums?.find(p =>
                                plan.uid ? p.plan_uid === plan.uid : p.plan_id === plan.plan_id
                            );
                            const previousPremium = existingPremium || findPlanDetails(plan, existingQuotation);
                            return {
                                uid: previousPremium.uid || '',
                                plan_id: plan.plan_id,
                                plan_uid: plan.uid || previousPremium.plan_uid || '',
                                insurer: previousPremium.insurer || '',
                                plan: previousPremium.plan || plan.label,
                                plan_label: previousPremium.plan_label || previousPremium.plan || plan.label,
                                included_benefits: previousPremium.included_benefits || '',
                                deductible: previousPremium.deductible || '',
                                area_of_coverage: previousPremium.area_of_coverage || '',
                                currency: previousPremium.currency || '',
                                payment_frequency: previousPremium.payment_frequency || '',
                                premium_rate: previousPremium.premium_rate ?? null,
                                ...(previousPremium.recommended && { recommended: previousPremium.recommended }),
                                ...previousPremium,
                            };
                        }),
                    };
                }),
            };
        });

        console.log(result);

        return result;
    }

    const [quotes, setQuotes] = page.useState('quotes', () => [
        createQuote('Quote 1: Nil Deductible'),
    ]);
    const [addQuoteOpen, setAddQuoteOpen] = useState(false);
    const [quoteToEdit, setQuoteToEdit] = useState(null);
    const [quoteToDelete, setQuoteToDelete] = useState(null);
    const [deleteQuoteOpen, setDeleteQuoteOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [deletePlanOpen, setDeletePlanOpen] = useState(false);

    function handleDeleteQuote(quote) {
        const nextQuotes = quotes.filter(q => q.uid !== quote.uid);
        setQuotes(nextQuotes);
        setQuotations(buildQuotations(nextQuotes, clients, plans, quotations));
        setPlans(prevPlans => prevPlans.map(p => {
            const { [quote.uid]: _, ...rest } = p.deductibles_per_quote || {};
            return { ...p, deductibles_per_quote: rest };
        }));
        markAllPlansStale();
    }

    function confirmDeleteQuote() {
        if (!quoteToDelete) {
            setDeleteQuoteOpen(false);
            return;
        }
        handleDeleteQuote(quoteToDelete);
        setQuoteToDelete(null);
        setDeleteQuoteOpen(false);
    }

    function confirmDeletePlan() {
        if (!planToDelete) {
            setDeletePlanOpen(false);
            return;
        }
        const nextPlans = plans.filter(p => getPlanKey(p) !== getPlanKey(planToDelete));
        handleDeletePlan(planToDelete);
        setQuotations(buildQuotations(quotes, clients, nextPlans, quotations));
        setPlanToDelete(null);
        setDeletePlanOpen(false);
    }

    function handleAddQuote({ name, id }) {
        if (id) {
            const nextQuotes = quotes.map(q => q.uid === id ? { ...q, name } : q);
            setQuotes(nextQuotes);
            setQuotations(buildQuotations(nextQuotes, clients, plans, quotations));
            setQuoteToEdit(null);
            return;
        }

        const newQuote = createQuote(name);
        const nextQuotes = [...quotes, newQuote];
        setQuotes(nextQuotes);
        setQuotations(buildQuotations(nextQuotes, clients, plans, quotations));
        markAllPlansStale();
    }

    const plansSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const tobPlansSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const [plans, setPlans] = page.useState('plans', []);
    const isShowingResult = plans.length > 0;
    const [addPlanOpen, setAddPlanOpen] = useState(false);
    const [manualPremiumOpen, setManualPremiumOpen] = useState(false);
    const [lifelineCompactOpen, setLifelineCompactOpen] = useState(false);
    const [bwhoCompactOpen, setBwhoCompactOpen] = useState(false);
    const [companyCompactOpen, setCompanyCompactOpen] = useState(false);
    const [oilGasCompactOpen, setOilGasCompactOpen] = useState(false);
    const [bhpIndonesiaCompactOpen, setBHPIndonesiaCompactOpen] = useState(false);
    const [proMedicoCompactOpen, setProMedicoCompactOpen] = useState(false);
    const [cignaCareConnectCompactOpen, setCignaCareConnectCompactOpen] = useState(false);
    const [ihhpCompactOpen, setIhhpCompactOpen] = useState(false);
    const [morganPriceCompactOpen, setMorganPriceCompactOpen] = useState(false);
    const [optimumGlobalCompactOpen, setOptimumGlobalCompactOpen] = useState(false);
    const [vumiCompactOpen, setVumiCompactOpen] = useState(false);
    const [myHealthPhilippinesCompactOpen, setMyHealthPhilippinesCompactOpen] = useState(false);
    const [myHealthSingaporeCompactOpen, setMyHealthSingaporeCompactOpen] = useState(false);
    const [myHealthIndonesiaCompactOpen, setMyHealthIndonesiaCompactOpen] = useState(false);
    const [cignaGlobalHealthOptionCompactOpen, setCignaGlobalHealthOptionCompactOpen] = useState(false);
    const [pacificCrossPrimaryCarePlanCompactOpen, setPacificCrossPrimaryCarePlanCompactOpen] = useState(false);
    const [pacificCrossSelectCompactOpen, setPacificCrossSelectCompactOpen] = useState(false);
    const [pacificCrossBlueRoyaleCompactOpen, setPacificCrossBlueRoyaleCompactOpen] = useState(false);
    const [hsbcSmartCareOptimumEnhancedCompactOpen, setHSBCSmartCareOptimumEnhancedCompactOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    // Opens the plan's dialog with its Click to Refresh showing, since its premiums are out of date.
    const isEditingPlanStale = !!editingPlan && stalePlanIds.has(getPlanKey(editingPlan));

    useEffect(() => {
        if (stalePlanIds.size > 0) {
            setNeedToSave(true);
        }

    }, [stalePlanIds]);

    function markAllPlansStale() {
        setStalePlanIds(prev => {
            if (plans.length === 0) return prev;
            return new Set(plans.map(getPlanKey));
        });
    }

    function handleEditPlan(plan) {
        setEditingPlan(plan);
        if (plan.plan_id === MANUAL_PREMIUM_PLAN_ID) {
            setManualPremiumOpen(true);
        } else if (plan.plan_id.startsWith('lifeline_')) {
            setLifelineCompactOpen(true);
        } else if (plan.plan_id.startsWith('bwho_')) {
            setBwhoCompactOpen(true);
        } else if (plan.plan_id.startsWith('company_')) {
            setCompanyCompactOpen(true);
        } else if (plan.plan_id.startsWith('oil_gas_')) {
            setOilGasCompactOpen(true);
        } else if (plan.plan_id.startsWith('bhp_indonesia_')) {
            setBHPIndonesiaCompactOpen(true);
        } else if (plan.plan_id.startsWith('pro_medico_')) {
            setProMedicoCompactOpen(true);
        } else if (plan.plan_id.startsWith('cigna_care_connect_')) {
            setCignaCareConnectCompactOpen(true);
        } else if (plan.plan_id.startsWith('ihhp_')) {
            setIhhpCompactOpen(true);
        } else if (plan.plan_id.startsWith('morgan_price_')) {
            setMorganPriceCompactOpen(true);
        } else if (plan.plan_id.startsWith('optimum_global_')) {
            setOptimumGlobalCompactOpen(true);
        } else if (plan.plan_id.startsWith('vumi_')) {
            setVumiCompactOpen(true);
        } else if (plan.plan_id.startsWith('my_health_philippines_')) {
            setMyHealthPhilippinesCompactOpen(true);
        } else if (plan.plan_id.startsWith('my_health_singapore_')) {
            setMyHealthSingaporeCompactOpen(true);
        } else if (plan.plan_id.startsWith('my_health_indonesia_')) {
            setMyHealthIndonesiaCompactOpen(true);
        } else if (plan.plan_id.startsWith('cigna_global_health_option_')) {
            setCignaGlobalHealthOptionCompactOpen(true);
        } else if (plan.plan_id.startsWith('pacific_cross_primary_care_plan_')) {
            setPacificCrossPrimaryCarePlanCompactOpen(true);
        } else if (plan.plan_id.startsWith('pacific_cross_select_')) {
            setPacificCrossSelectCompactOpen(true);
        } else if (plan.plan_id.startsWith('pacific_cross_blue_royale_')) {
            setPacificCrossBlueRoyaleCompactOpen(true);
        } else if (plan.plan_id.startsWith('hsbc_smart_care_optimum_enhanced_')) {
            setHSBCSmartCareOptimumEnhancedCompactOpen(true);
        }
    }

    function handleDeletePlan(plan) {
        const nextPlans = plans.filter(p => getPlanKey(p) !== getPlanKey(plan));
        setPlans(nextPlans);
        setQuotations(buildQuotations(quotes, clients, nextPlans, quotations));

        setTobPlans(prev => prev.filter(tob => !isTobForPlan(prev, tob, plan)));
    }

    // TOB plans share their plan's key as uid. Records saved before that have random TOB
    // uids, so when no TOB carries the plan's key it falls back to the old tier match.
    function isTobForPlan(tobs, tob, plan) {
        const planKey = getPlanKey(plan);
        if (tobs.some(t => t.uid === planKey)) return tob.uid === planKey;
        return tob.id === plan.plan_id || tob.id === normalizeTobId(plan.plan_id, plan.payloads);
    }

    function addCorrespondingTob(plan, currency, areaOfCoverage) {
        const normalizedId = normalizeTobId(plan.plan_id, plan.payloads);

        // if (tobPlans.some(p => p.id === normalizedId)) return;
        if (!isUniversalTobId(normalizedId)) {
            // The labels column sets the table's rows, so the placeholder has nothing to span without it.
            loadTobBenefitLabels();
            upsertTobPlan(createTobPlaceholder({ id: plan.plan_id, label: plan.label, uid: getPlanKey(plan) }));
            return;
        }

        handleAddTob({ id: plan.plan_id, label: plan.label, uid: getPlanKey(plan) }, normalizedId, currency, areaOfCoverage);
    }

    // Keeps an already-added TOB plan's Currency (row 4) / Area of Coverage (row 5) in sync
    // when the corresponding Compact_Rates_Dialog plan is edited.
    function updateTobPlanFields(plan, currency, areaOfCoverage) {
        if (!currency && !areaOfCoverage) return;

        setTobPlans(prev => prev.map(tob => {
            if (tob.placeholder || !isTobForPlan(prev, tob, plan)) return tob;
            let table = tob.table;
            if (currency) table = setTobTableRowValue(table, 4, currency);
            if (areaOfCoverage) table = setTobTableRowValue(table, 5, normalizeAreaOfCoverDisplay(areaOfCoverage, tobLanguage));
            return { ...tob, table };
        }));
    }

    function addOrReplacePlan(plan) {
        // A fresh plan.uid never matches an existing plan's uid, so a brand-new addition is
        // always appended even when it shares a plan_id (tier) with an existing plan; editing
        // reuses the original uid and correctly replaces that instance in place. Plans without
        // a uid (from insurers that don't support duplicates) still match by plan_id.
        const matchesPlan = p => plan.uid ? p.uid === plan.uid : p.plan_id === plan.plan_id;
        // Decided from the rendered plans, not inside the setPlans updater: React may defer
        // that updater to the next render, which left this true on every edit and made each
        // Update Premiums add a fresh TOB plan (with a new uid) instead of updating it.
        const existingPlan = plans.find(matchesPlan);
        const isNewPlan = !existingPlan;
        // Some edits move the plan onto another TOB column (e.g. a Pacific Cross Select room
        // type change), so that TOB is fetched again rather than only having its fields synced.
        const tobIdChanged = !isNewPlan
            && normalizeTobId(existingPlan.plan_id, existingPlan.payloads) !== normalizeTobId(plan.plan_id, plan.payloads);
        setPlans(prevPlans => {
            if (prevPlans.some(matchesPlan)) {
                return prevPlans.map(p => matchesPlan(p) ? plan : p);
            }
            return [...prevPlans, plan];
        });
        setStalePlanIds(prev => {
            const next = new Set(prev);
            next.delete(getPlanKey(plan));
            return next;
        });
        if (isNewPlan || tobIdChanged) {
            addCorrespondingTob(plan, plan.payloads?.currency, plan.payloads?.area_of_coverage);
        } else {
            updateTobPlanFields(plan, plan.payloads?.currency, plan.payloads?.area_of_coverage);
        }
    }

    function handlePlansDragEnd(event) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = plans.findIndex(p => getPlanKey(p) === active.id);
        const newIndex = plans.findIndex(p => getPlanKey(p) === over.id);
        const nextPlans = arrayMove(plans, oldIndex, newIndex);
        setPlans(nextPlans);

        // TOB plans, plans and premiums share the plan key, so all three follow one order.
        const planIdOrder = nextPlans.map(p => p.plan_id);
        const planKeyOrder = nextPlans.map(getPlanKey);

        // Reorder tobPlans to match the new plans order; TOBs from records saved before they
        // shared the plan key fall back to the tier's plan_id.
        setTobPlans(prev => {
            const tobIndex = tob => {
                const keyIndex = planKeyOrder.indexOf(tob.uid);
                return keyIndex !== -1 ? keyIndex : planIdOrder.indexOf(tob.id);
            };
            return [...prev].sort((a, b) => {
                const ai = tobIndex(a);
                const bi = tobIndex(b);
                return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
            });
        });

        // Reorder premiums inside each client_premiums to match the new plans order
        setQuotations(prev => prev.map(quotation => ({
            ...quotation,
            client_premiums: quotation.client_premiums.map(cp => {
                const sorted = [...cp.premiums].sort((a, b) => {
                    const ai = planKeyOrder.indexOf(a.plan_uid || a.plan_id);
                    const bi = planKeyOrder.indexOf(b.plan_uid || b.plan_id);
                    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
                });
                return { ...cp, premiums: sorted };
            }),
        })));
    }

    function handleTobPlansDragEnd(event) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = tobPlans.findIndex(p => p.uid === active.id);
        const newIndex = tobPlans.findIndex(p => p.uid === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const nextTobPlans = arrayMove(tobPlans, oldIndex, newIndex);
        setTobPlans(nextTobPlans);

        const tobUidOrder = nextTobPlans.map(p => p.uid);
        const tobIdOrder = nextTobPlans.map(p => p.id);
        // A TOB's uid is its plan's key; TOBs from records saved before that fall back to tier.
        const orderIndex = (key, planId) => {
            const uidIndex = tobUidOrder.indexOf(key);
            return uidIndex !== -1 ? uidIndex : tobIdOrder.indexOf(planId);
        };

        // Reorder plans to match the new tobPlans order
        setPlans(prev => {
            return [...prev].sort((a, b) => {
                const ai = orderIndex(getPlanKey(a), a.plan_id);
                const bi = orderIndex(getPlanKey(b), b.plan_id);
                return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
            });
        });

        // Reorder premiums inside each client_premiums to match the new order
        setQuotations(prev => prev.map(quotation => ({
            ...quotation,
            client_premiums: quotation.client_premiums.map(cp => {
                const sorted = [...cp.premiums].sort((a, b) => {
                    const ai = orderIndex(a.plan_uid || a.plan_id, a.plan_id);
                    const bi = orderIndex(b.plan_uid || b.plan_id, b.plan_id);
                    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
                });
                return { ...cp, premiums: sorted };
            }),
        })));
    }

    function handleAddPlan(plan) {
        const newPlan = { plan_id: plan.id, label: plan.label, payloads: {}, deductibles_per_quote: {} };
        const nextPlans = [...plans, newPlan];
        setPlans(nextPlans);
        setQuotations(buildQuotations(quotes, clients, nextPlans, quotations));
        addCorrespondingTob(newPlan);
    }

    function handleMyHealthIndonesiaAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleMyHealthSingaporeAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleMyHealthPhilippinesAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleVumiAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleOptimumGlobalAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleCignaGlobalHealthOptionAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    // Match on plan_uid (the specific plan instance) when the update carries one,
                    // so editing one of several same-tier plans (e.g. two Silvers) doesn't merge
                    // into the wrong row; updates from plans without a uid fall back to plan_id.
                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handlePacificCrossPrimaryCarePlanAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handlePacificCrossSelectAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handlePacificCrossBlueRoyaleAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleMorganPriceAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleIhhpAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleBwhoAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleCompanyAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleCignaCareConnectAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleHSBCSmartCareOptimumEnhancedAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleProMedicoAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleBHPIndonesiaAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleOilGasAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            return next;
        });
    }

    function handleLifelineAddToQuotations(updates, plan) {
        if (plan) {
            addOrReplacePlan(plan);
        }
        setQuotations(prev => {
            const base = prev.length > 0 ? prev : buildQuotations(quotes, clients, plans, []);
            console.log('[handleLifelineAddToQuotations] base:', JSON.parse(JSON.stringify(base)));
            console.log('[handleLifelineAddToQuotations] updates:', updates);
            const next = base.map(quotation => ({
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => {
                    const clientUpdates = updates.filter(u => u.quoteUid === quotation.uid && u.clientUid === cp.client_uid);
                    if (clientUpdates.length === 0) return cp;

                    // Update existing premiums
                    const updatedPremiums = cp.premiums.map(premium => {
                        const update = clientUpdates.find(u =>
                            u.planUid ? u.planUid === premium.plan_uid : u.planId === premium.plan_id
                        );
                        if (!update) return premium;
                        return { ...premium, ...update.premium, plan_uid: update.planUid || premium.plan_uid || '' };
                    });

                    // Append new premiums for plans not yet in the list
                    const existingKeys = new Set(cp.premiums.map(p => p.plan_uid || p.plan_id));
                    const newPremiums = clientUpdates
                        .filter(u => !existingKeys.has(u.planUid || u.planId))
                        .map(u => ({
                            uid: uuidv4(),
                            plan_id: u.planId,
                            plan_uid: u.planUid || '',
                            ...u.premium,
                        }));

                    return {
                        ...cp,
                        premiums: [...updatedPremiums, ...newPremiums],
                    };
                }),
            }));
            console.log('[handleLifelineAddToQuotations] next:', JSON.parse(JSON.stringify(next)));
            return next;
        });
    }

    // Applies `update(premium, fields)` to every premium the Quotation Preview cells point at.
    // A target with no clientUid is a shared row (insurer, deductible, ...) kept the same across
    // every client's premium for that plan; a rate cell targets one client's premium.
    function updatePreviewPremiums(quotationUid, targets, update) {
        setQuotations(prev => prev.map(quotation => {
            if (quotation.uid !== quotationUid) return quotation;
            return {
                ...quotation,
                client_premiums: quotation.client_premiums.map(cp => ({
                    ...cp,
                    premiums: cp.premiums.map((premium, idx) => {
                        const fields = targets
                            .filter(target => target.planIdx === idx && (!target.clientUid || target.clientUid === cp.client_uid))
                            .map(target => target.field);
                        return fields.length > 0 ? update(premium, fields) : premium;
                    }),
                })),
            };
        }));
    }

    // Anything but the included benefits note is overwritten by the plan's next Update
    // Premiums, so those edits mark the premium as edited until fresh rates replace it.
    // Manual Premium plans are typed in by the user to begin with, so they are never flagged.
    function handleUpdatePreviewCell(quotationUid, target, value) {
        updatePreviewPremiums(quotationUid, [target], premium => ({
            ...premium,
            [target.field]: value,
            ...(target.field !== 'included_benefits' && premium.plan_id !== MANUAL_PREMIUM_PLAN_ID && { edited: true }),
        }));
        syncManualPremiumPlanName(quotationUid, target, value);
        copyManualPremiumFromFirstQuote(quotationUid, target, value);
    }

    // As in the Manual Premium dialog: what is typed into the first quote's insurer, plan or
    // area of cover is copied into the other quotes that are still empty for that plan.
    // Like the dialog's blur, this runs when the cell is committed, not per keystroke.
    function copyManualPremiumFromFirstQuote(quotationUid, target, value) {
        if (!MANUAL_PREMIUM_COPIED_FIELDS.includes(target.field) || !String(value).trim()) return;
        if (quotationUid !== quotes[0]?.uid) return;

        const quotation = quotations.find(q => q.uid === quotationUid);
        const premium = quotation?.client_premiums?.[0]?.premiums?.[target.planIdx];
        if (premium?.plan_id !== MANUAL_PREMIUM_PLAN_ID || !premium.plan_uid) return;

        setQuotations(prev => prev.map(q => (q.uid === quotationUid ? q : {
            ...q,
            client_premiums: q.client_premiums.map(cp => ({
                ...cp,
                premiums: cp.premiums.map(p => (
                    p.plan_uid === premium.plan_uid && !p.blank && !String(p[target.field] || '').trim()
                        ? { ...p, [target.field]: value }
                        : p
                )),
            })),
        })));
    }

    // A Manual Premium plan is named after its insurer and plan, so typing either into the
    // preview renames its plan chip and TOB placeholder, as saving the dialog would.
    function syncManualPremiumPlanName(quotationUid, target, value) {
        if (target.field !== 'insurer' && target.field !== 'plan_label') return;

        const quotation = quotations.find(q => q.uid === quotationUid);
        const premium = quotation?.client_premiums?.[0]?.premiums?.[target.planIdx];
        if (premium?.plan_id !== MANUAL_PREMIUM_PLAN_ID || !premium.plan_uid) return;

        const insurer = (target.field === 'insurer' ? value : premium.insurer || '').trim();
        const planName = (target.field === 'plan_label' ? value : premium.plan_label || '').trim();
        const label = `${insurer} ${planName}`.trim() || MANUAL_PREMIUM_DEFAULT_LABEL;

        setPlans(prev => prev.map(p => (p.uid === premium.plan_uid ? { ...p, insurer, plan: planName, label } : p)));
        setTobPlans(prev => prev.map(tob => (tob.uid === premium.plan_uid && tob.placeholder ? { ...tob, label } : tob)));
    }

    // The "+" on the Quotation Preview's right edge adds an empty Manual Premium column to
    // every quote, to be typed into like a spreadsheet instead of filled through the dialog
    // (which still opens from its plan chip). Returns the new plan's uid so the table can
    // start editing it.
    function handleAddInlineManualPremium() {
        refInputTable.current?.getClientData();
        if (!refInputTable.current?.validateForm()) {
            toast.error('Please fill in all required client fields.', { hideProgressBar: true });
            return null;
        }

        const planUid = uuidv4();
        const updates = quotes.flatMap(quote => clients.map(client => ({
            quoteUid: quote.uid,
            clientUid: client.uid,
            planId: MANUAL_PREMIUM_PLAN_ID,
            planUid,
            premium: {
                uid: uuidv4(),
                plan_id: MANUAL_PREMIUM_PLAN_ID,
                plan_uid: planUid,
                insurer: '',
                plan: '',
                plan_label: '',
                included_benefits: '',
                deductible: '',
                area_of_coverage: '',
                currency: 'USD',
                payment_frequency: 'Annually',
                premium_rate: null,
                discount: '0',
                loading: '0',
                blank: false,
            },
        })));
        const plan = {
            plan_id: MANUAL_PREMIUM_PLAN_ID,
            uid: planUid,
            plan: '',
            insurer: '',
            label: MANUAL_PREMIUM_DEFAULT_LABEL,
            payloads: { area_of_coverage: '', currency: 'USD', payment_frequency: 'Annually' },
            deductibles_per_quote: Object.fromEntries(quotes.map(quote => [quote.uid, ''])),
        };

        handleLifelineAddToQuotations(updates, plan);
        return planUid;
    }

    // Formatting lives on the premium under cell_styles, keyed by the field the cell shows.
    // These are only overrides of the row's own look, so clearing one (an undefined value)
    // drops it and the cell goes back to its default.
    function handleFormatPreviewCells(quotationUid, targets, patch) {
        updatePreviewPremiums(quotationUid, targets, (premium, fields) => ({
            ...premium,
            cell_styles: fields.reduce((styles, field) => {
                const fieldStyles = { ...styles[field], ...patch };
                Object.keys(patch).forEach(key => {
                    if (patch[key] === undefined) delete fieldStyles[key];
                });
                return { ...styles, [field]: fieldStyles };
            }, premium.cell_styles || {}),
        }));
    }

    function handleUpdateRecommended(quotationUid, planIdx, checked) {
        setQuotations(prev => prev.map(quotation => ({
            ...quotation,
            client_premiums: quotation.client_premiums.map(cp => ({
                ...cp,
                premiums: cp.premiums.map((premium, idx) => {
                    const { recommended, ...rest } = premium;
                    if (checked && quotation.uid === quotationUid && idx === planIdx) {
                        return { ...rest, recommended: true };
                    }
                    return rest;
                }),
            })),
        })));
    }

    // { planUid, rows: ['7', '8', ...] } -- planUid holds a tobPlan's uid, always within one column.
    const [tobSelection, setTobSelection] = useState(null);
    // The formatting bar only shows while the TOB table is hovered and has a selection.
    const [tobTableHovered, setTobTableHovered] = useState(false);
    const showTobMenuBar = tobTableHovered && (tobSelection?.rows.length || 0) > 0;

    function handleUpdateTobCell(planUid, rowNum, value) {
        setTobPlans(prev => prev.map(plan => (
            plan.uid === planUid ? { ...plan, table: patchCell(plan.table, rowNum, { cell_value: value }) } : plan
        )));
    }

    // The toolbar shows the state of the first cell in the selection and applies it to all.
    const selectedTobCellData = tobSelection
        ? readCell(tobPlans.find(plan => plan.uid === tobSelection.planUid)?.table, tobSelection.rows[0])
        : null;

    function formatTobSelection(patch) {
        if (!tobSelection) return;

        setTobPlans(prev => prev.map(plan => {
            if (plan.uid !== tobSelection.planUid) return plan;
            const table = tobSelection.rows.reduce((acc, rowNum) => patchCellStyle(acc, rowNum, patch), plan.table);
            return { ...plan, table };
        }));
    }

    const selectedTobPlanUid = tobSelection?.planUid || null;
    const tobSelectionRange = tobSelection && {
        start: Math.min(...tobSelection.rows.map(Number)),
        end: Math.max(...tobSelection.rows.map(Number)),
    };
    const overlappingTobMerges = selectedTobPlanUid
        ? (tobPlans.find(plan => plan.uid === selectedTobPlanUid)?.vertical_merge_cells || [])
            .filter(merge => merge.end_row >= tobSelectionRange.start && merge.start_row <= tobSelectionRange.end)
        : [];

    const canMergeTob = Boolean(selectedTobPlanUid) && tobSelection.rows.length > 1;
    const canUnmergeTob = overlappingTobMerges.length > 0;

    // Anything the new span overlaps is replaced by it, so the ranges never interleave.
    function updateTobMerges(updater) {
        const plan = tobPlans.find(p => p.uid === selectedTobPlanUid);
        if (!plan) return;

        const kept = (plan.vertical_merge_cells || []).filter(merge => (
            merge.end_row < tobSelectionRange.start || merge.start_row > tobSelectionRange.end
        ));

        setTobPlans(prev => prev.map(p => (p.uid === selectedTobPlanUid ? { ...p, vertical_merge_cells: updater(kept) } : p)));
    }

    function handleMergeTob() {
        if (!canMergeTob) return;

        updateTobMerges(kept => [...kept, { col: 1, start_row: tobSelectionRange.start, end_row: tobSelectionRange.end }]
            .sort((a, b) => a.start_row - b.start_row));

        // Only the first row of a merge is rendered, so the rest can no longer be clicked.
        setTobSelection({ planUid: selectedTobPlanUid, rows: [String(tobSelectionRange.start)] });
    }

    function handleUnmergeTob() {
        if (!canUnmergeTob) return;
        // updateTobMerges already drops every span the selection touches, so keeping only what
        // it hands back is the unmerge.
        updateTobMerges(kept => kept);
    }

    async function handleCreateEmailDraft(data) {
        console.log('[handleCreateEmailDraft] data:', data);
        setCreatingEmailDraft(true);
        try {
            const { body, styles, from, to, cc, subject, quotation_table_html, tob_table_html } = data;

            const emailPayload = {
                email_from: from,
                email_to: to,
                email_cc: cc,
                email_subject: subject,
                email_body: body,
                email_styles: styles,
                email_quotation_table_html: quotation_table_html,
                email_tob_table_html: tob_table_html
            };

            const record = await saveAutoQuotationRecord({
                showToast: false,
                shouldNavigate: false,
                additionalPayload: emailPayload
            });

            if (!record) {
                return;
            }

            const finalData = {
                ...data,
                auto_quotation_record_id: record.id,
                body: wrapEmailHtml(body, styles)
            };

            const response = await EmailDraftService.createDraft(finalData, auth.token);
            if (response.data.status === 'success') {
                setEmailDialogOpen(false);
                // Delay to allow dialog closure animation
                await new Promise(resolve => setTimeout(resolve, 300));
                toast.success('Email draft created.', { hideProgressBar: true, autoClose: 3000 });
                navigate(`/auto_quotation/${record.id}`);
            } else {
                toast.error('Failed to create email draft: ' + (response.data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error creating email draft:', error);
            toast.error('Error creating email draft. Please try again.');
        } finally {
            setCreatingEmailDraft(false);
        }
    }

    // PDF and Excel are built from the same table HTML, so they share the save/fetch/download flow.
    const downloadQuotationFile = async ({ request, extension, label, setConfirmOpen, setDownloading }) => {
        setConfirmOpen(false);
        // Delay to allow dialog closure animation
        await new Promise(resolve => setTimeout(resolve, 300));

        setDownloading(true);
        try {
            const record = await saveAutoQuotationRecord({
                showToast: false,
                shouldNavigate: false
            });

            if (!record) {
                return;
            }

            const quotation_table_html = getLiveTableHTML('Quotations');
            const tob_table_html = getLiveTableHTML('Table of benefits');

            const payload = {
                quotation_table_html,
                tob_table_html,
                styles: EMAIL_STYLES,
                auto_quotation_record_id: record.id
            };

            const response = await request(payload, auth.token);

            // Create a blob from the response and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${clients[0]?.full_name || 'Client'}_Quotation.${extension}`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // toast.success(`${label} downloaded.`, { hideProgressBar: true, autoClose: 3000 });
            navigate(`/auto_quotation/${record.id}`);
        } catch (error) {
            console.error(`Error downloading ${label}:`, error);
            toast.error(`Failed to download ${label}. Please try again.`);
        } finally {
            setDownloading(false);
            setConfirmOpen(false);
        }
    };

    const handleDownloadPdf = () => downloadQuotationFile({
        request: downloadQuotationPdf,
        extension: 'pdf',
        label: 'PDF',
        setConfirmOpen: setDownloadPdfConfirmOpen,
        setDownloading: setDownloadingPdf,
    });

    const handleDownloadExcel = () => downloadQuotationFile({
        request: downloadQuotationExcel,
        extension: 'xlsx',
        label: 'Excel',
        setConfirmOpen: setDownloadExcelConfirmOpen,
        setDownloading: setDownloadingExcel,
    });

    return (
        <Box sx={{ display: 'flex' }}>
            <MainHeader open={open} setOpen={setOpen} blockNavigation={loadingTobBenefits} blockNavigationMessage='Please wait for the TOB table to finish loading.' />
            <Main open={open} sx={{ pt: 7 }}>
                <Container maxWidth="xl">
                    <Typography variant='h4' sx={{ fontWeight: 600, mt: 4, mb: 2 }}>Auto Quotation</Typography>
                    <ActionMenuBar label="Quotation actions" sx={{ mb: 4 }}>
                        <Button role="menuitem" aria-haspopup="true" aria-expanded={Boolean(quoteHistoryAnchor)} size="small" startIcon={<HistoryIcon />} endIcon={<ArrowDropDownIcon />} onClick={(e) => setQuoteHistoryAnchor(e.currentTarget)} sx={{ ...(selectedQuotation && { fontWeight: 600 }) }}>{selectedQuotation ? `${selectedQuotation.provided_for}'s Quotation (${selectedQuotation.quote_no})` : 'Quotation History'}</Button>
                        <AutoQuotationHistory
                            open={Boolean(quoteHistoryAnchor)}
                            anchorEl={quoteHistoryAnchor}
                            onClose={() => setQuoteHistoryAnchor(null)}
                            onSelect={(record) => {
                                loadAutoQuotationRecord(record.id);
                                navigate(`/auto_quotation/${record.id}`);
                                setQuoteHistoryAnchor(null);
                            }}
                            onNew={() => {
                                requestNewQuotation();
                            }}
                            onDelete={handleAutoQuotationRecordDelete}
                            token={auth.token}
                            selectedRecordId={selectedQuotation?.id}
                        />
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Badge color="warning" variant="dot" invisible={!needToSave} sx={{ '& .MuiBadge-badge': { fontWeight: 'bold', backgroundColor: '#fbc02d' } }}>
                            <Button role="menuitem" size="small" startIcon={<SaveIcon />} disabled={savingAutoQuotationRecord} onClick={() => {
                                if (refInputTable.current?.validateForm()) {
                                    setSaveConfirmOpen(true);
                                } else {
                                    toast.error('Please fill in all required client fields.', { hideProgressBar: true });
                                }
                            }}>
                                Save
                                {savingAutoQuotationRecord && <CircularProgress size={16} sx={{ color: '#fff', ml: 1 }} />}
                            </Button>
                        </Badge>
                        <Button role="menuitem" size="small" startIcon={<NoteAddIcon />} onClick={requestNewQuotation}>New Quotation</Button>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Button
                            role="menuitem"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => {
                                if (plans.length === 0) {
                                    toast.error('Add at least one plan before drafting an email.', { hideProgressBar: true });
                                    return;
                                }
                                setEmailDialogOpen(true);
                            }}
                        >
                            Draft Email
                        </Button>
                        <Button
                            role="menuitem"
                            aria-haspopup="menu"
                            aria-expanded={Boolean(downloadMenuAnchor)}
                            size="small"
                            startIcon={<DownloadIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            disabled={downloadingPdf || downloadingExcel}
                            onClick={(e) => {
                                if (plans.length === 0) {
                                    toast.error('Add at least one plan before downloading.', { hideProgressBar: true });
                                    return;
                                }
                                setDownloadMenuAnchor(e.currentTarget);
                            }}
                        >
                            Download
                            {(downloadingPdf || downloadingExcel) && <CircularProgress size={16} sx={{ color: '#fff', ml: 1 }} />}
                        </Button>
                        <Menu
                            anchorEl={downloadMenuAnchor}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            open={Boolean(downloadMenuAnchor)}
                            onClose={() => setDownloadMenuAnchor(null)}
                        >
                            <MenuItem onClick={() => { setDownloadMenuAnchor(null); setDownloadPdfConfirmOpen(true); }}>
                                <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>PDF</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => { setDownloadMenuAnchor(null); setDownloadExcelConfirmOpen(true); }}>
                                <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Excel</ListItemText>
                            </MenuItem>
                        </Menu>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Button
                            role="menuitem"
                            aria-haspopup="menu"
                            aria-expanded={Boolean(tobLanguageMenuAnchor)}
                            size="small"
                            startIcon={<TranslateIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            disabled={loadingTobBenefits}
                            onClick={(e) => setTobLanguageMenuAnchor(e.currentTarget)}
                        >
                            {tobLanguage}
                        </Button>
                        <Menu
                            anchorEl={tobLanguageMenuAnchor}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            open={Boolean(tobLanguageMenuAnchor)}
                            onClose={() => setTobLanguageMenuAnchor(null)}
                        >
                            {TOB_LANGUAGES.map(language => (
                                <MenuItem key={language} selected={language === tobLanguage} onClick={() => requestTobLanguageChange(language)}>
                                    <ListItemText>{language}</ListItemText>
                                </MenuItem>
                            ))}
                        </Menu>
                    </ActionMenuBar>
                    {loadingAutoQuotationRecord ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                            <CircularProgress sx={{ color: '#673ab7' }} />
                        </Box>
                    ) : (
                        <>


                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                                <Typography sx={SECTION_HEADER_SX}>Clients</Typography>
                                <Button endIcon={<PersonAddAlt1Icon />} variant='contained' size="small" onClick={handleAddClient} sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: BUTTONS_MIN_WIDTH }}>Add Client</Button>
                            </Box>
                            <InputTable
                                ref={refInputTable}
                                clients={clients}
                                setClients={setClients}
                                relationships={relationships}
                                setCountryOfResidence={setCountryOfResidence}
                                setNationality={setNationality}
                                setShowRefreshNow={setShowRefreshNow}
                                isShowingResult={isShowingResult}
                                setShowRemoveClient={setShowRemoveClient}
                                setRemoveClientIndex={setRemoveClientIndex}
                                setClientsUpdated={setClientsUpdated}
                                onClientsInput={handleClientsInput}
                            />

                            {/* Quotes */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 4, mb: 1 }}>
                                        <Typography sx={SECTION_HEADER_SX}>Quotes</Typography>
                                    </Box>

                                    <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {quotes.map(quote => (
                                            <Chip size="small"
                                                key={quote.uid}
                                                label={quote.name}
                                                onClick={() => {
                                                    setQuoteToEdit(quote);
                                                    setAddQuoteOpen(true);
                                                }}
                                                onDelete={() => {
                                                    if (quotes.length <= 1) {
                                                        toast.error('At least one quote is required', { hideProgressBar: true });
                                                        return;
                                                    }
                                                    setQuoteToDelete(quote);
                                                    setDeleteQuoteOpen(true);
                                                }}
                                                sx={{ backgroundColor: '#7e57c2', color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }}
                                            />
                                        ))}
                                        <Chip size="small" variant="outlined" icon={<AddIcon sx={{ color: '#7e57c2 !important' }} />} label="Add Quote" onClick={() => { if (refInputTable.current?.validateForm()) setAddQuoteOpen(true); else toast.error('Please fill in all required client fields.', { hideProgressBar: true }); }} sx={{ backgroundColor: '#f5f5f5', color: '#7e57c2', borderStyle: 'dashed', borderColor: '#7e57c2', borderWidth: 1 }} />
                                    </Paper>
                                </Box>

                                {/* Plans */}
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 4, mb: 1 }}>
                                        <Typography sx={SECTION_HEADER_SX}>Plans</Typography>
                                        {/* <Button variant='contained' size="small" onClick={handleAddClient} sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: BUTTONS_MIN_WIDTH }}>Add Plan</Button> */}
                                    </Box>

                                    <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <DndContext sensors={plansSensors} collisionDetection={closestCenter} onDragEnd={handlePlansDragEnd}>
                                            <SortableContext items={plans.map(getPlanKey)} strategy={horizontalListSortingStrategy}>
                                                {plans.map(plan => (
                                                    <SortablePlanChip
                                                        key={getPlanKey(plan)}
                                                        plan={plan}
                                                        onEdit={() => handleEditPlan(plan)}
                                                        onDelete={() => {
                                                            setPlanToDelete(plan);
                                                            setDeletePlanOpen(true);
                                                        }}
                                                        isStale={stalePlanIds.has(getPlanKey(plan))}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                        <Chip size="small" variant="outlined" icon={<AddIcon sx={{ color: '#7e57c2 !important' }} />} label="Add Plan" onClick={() => { if (refInputTable.current?.validateForm()) setAddPlanOpen(true); else toast.error('Please fill in all required client fields.', { hideProgressBar: true }); }} sx={{ backgroundColor: '#f5f5f5', color: '#7e57c2', borderStyle: 'dashed', borderColor: '#7e57c2', borderWidth: 1 }} />
                                    </Paper>
                                </Box>
                            </Box>


                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 4, mb: 1 }}>
                                <Typography sx={SECTION_HEADER_SX}>Quotation Preview</Typography>
                                {/* <Button endIcon={<SyncIcon />} variant='contained' size="small" onClick={handleAddClient} sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: BUTTONS_MIN_WIDTH }}>View Premiums</Button> */}
                            </Box>

                            <Paper sx={{ p: 2 }} id="quotation-preview-container">
                                <QuotationPreviewTable quotations={quotations} clients={clients} onUpdateCell={handleUpdatePreviewCell} onFormatCells={handleFormatPreviewCells} onUpdateRecommended={handleUpdateRecommended} onAddManualPremium={handleAddInlineManualPremium}
                                    stalePlanKeys={stalePlanIds}
                                    language={tobLanguage}
                                    onOpenPlan={planKey => {
                                        const plan = plans.find(p => getPlanKey(p) === planKey);
                                        if (plan) handleEditPlan(plan);
                                    }}
                                />
                                {/* <hr style={{ margin: '16px 0' }} />
                                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(quotations, null, 2)}</pre>
                                <hr />
                                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(plans, null, 2)}</pre>
                                <hr />
                                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(tobPlans.map(({ table, vertical_merge_cells, ...rest }) => rest), null, 2)}</pre> */}
                            </Paper>

                            {/* TOB */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 4, mb: 1 }}>
                                <Typography sx={SECTION_HEADER_SX}>Table Of Benefits</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button  endIcon={<LibraryAddIcon />}  variant='contained' size="medium" onClick={handleOpenAddCustomTobPlan} sx={{ backgroundColor: '#7b1fa2', textTransform: 'none', '&:hover': { backgroundColor: '#6a1b9a' }, minWidth: BUTTONS_MIN_WIDTH }}>My TOB Plans</Button>
                                </Box>
                            </Box>

                            {tobPlans.length > 0 && (
                                <TOBModuleOptions
                                    modules={TOB_MODULES}
                                    selectedModules={selectedTobModules}
                                    onToggleModule={handleToggleTobModule}
                                />
                            )}

                            <Paper sx={{ p: 2 }} id="tob-table-container" onMouseEnter={() => setTobTableHovered(true)} onMouseLeave={() => setTobTableHovered(false)}>
                                <Box className="remove-in-email" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    <DndContext sensors={tobPlansSensors} collisionDetection={closestCenter} onDragEnd={handleTobPlansDragEnd}>
                                        <SortableContext items={tobPlans.map(plan => plan.uid)} strategy={horizontalListSortingStrategy}>
                                            {tobPlans.map(plan => (
                                                <SortableTobChip key={plan.uid} plan={plan} onDelete={() => handleRequestDeleteTobPlan(plan)} />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                    <Chip size="small" variant="outlined" icon={<AddIcon sx={{ color: '#7e57c2 !important' }} />} label="Add TOB" onClick={handleOpenAddTobDialog} sx={{ backgroundColor: '#f5f5f5', color: '#7e57c2', borderStyle: 'dashed', borderColor: '#7e57c2', borderWidth: 1 }} />
                                </Box>
                                {tobPlans.length > 0 && (
                                    <Box className="remove-in-email" sx={{ position: 'sticky', top: 56, zIndex: 2, backgroundColor: '#fff', transition: 'opacity 150ms', ...(!showTobMenuBar && { opacity: 0, visibility: 'hidden', pointerEvents: 'none' }) }}>
                                        <TOBTableMenuBar
                                            cell={selectedTobCellData}
                                            selectedCount={tobSelection?.rows.length || 0}
                                            canMerge={canMergeTob}
                                            canUnmerge={canUnmergeTob}
                                            onToggleBold={() => formatTobSelection({ font_weight: isBoldCell(selectedTobCellData) ? '400' : '700' })}
                                            onSetTextColor={color => formatTobSelection({ cell_text_color: color })}
                                            onSetBackgroundColor={color => formatTobSelection({ cell_bg_color: color })}
                                            onMerge={handleMergeTob}
                                            onUnmerge={handleUnmergeTob}
                                        />
                                    </Box>
                                )}
                                <TOBTable
                                    tobPlans={tobPlans}
                                    language={tobLanguage}
                                    tobBenefitLabelsColumn={tobBenefitLabelsColumn}
                                    visibleRowNumbers={visibleTobRowNumbers}
                                    editable
                                    onUpdateCell={handleUpdateTobCell}
                                    selection={tobSelection}
                                    onSelectCells={setTobSelection}
                                    onAddPlaceholderTob={handleOpenFillTobPlaceholder}
                                />
                            </Paper>
                        </>
                    )}

                    <AddQuoteDialog
                        open={addQuoteOpen}
                        onClose={() => {
                            setAddQuoteOpen(false);
                            setQuoteToEdit(null);
                        }}
                        onSubmit={handleAddQuote}
                        existingNames={quotes.map(q => q.name)}
                        initialName={quoteToEdit?.name || ''}
                        quoteId={quoteToEdit?.uid || ''}
                    />

                    <AlertDialog
                        open={deleteQuoteOpen}
                        setOpen={setDeleteQuoteOpen}
                        title='Remove Quote'
                        content={quoteToDelete ? `Please confirm removing the quote <strong>${quoteToDelete.name}</strong>.` : 'Please confirm removing this quote.'}
                        positiveButtonLabel='Remove'
                        negativeButtonLabel='Cancel'
                        callback={confirmDeleteQuote}
                    />

                    <AlertDialog
                        open={saveConfirmOpen}
                        setOpen={setSaveConfirmOpen}
                        title='Save Auto Quotation'
                        content={selectedQuotation ? 'Do you want to save the updates?' : 'Do you want to save this auto quotation?'}
                        positiveButtonLabel='Save'
                        negativeButtonLabel='Cancel'
                        callback={handleSaveAutoQuotationRecord}
                    />

                    <AlertDialog
                        open={newQuotationConfirmOpen}
                        setOpen={setNewQuotationConfirmOpen}
                        title='New Quotation'
                        content='Please save your changes before creating a new quotation. Click continue to proceed without saving.'
                        positiveButtonLabel='Continue'
                        negativeButtonLabel='Cancel'
                        callback={confirmNewQuotation}
                    />

                    <AddPlanDialog
                        open={addPlanOpen}
                        onClose={() => setAddPlanOpen(false)}
                        onSubmit={handleAddPlan}
                        existingPlanIds={plans.map(p => p.plan_id)}
                        onSelectManualPremium={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setManualPremiumOpen(true);
                        }}
                        onSelectLifeline={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setLifelineCompactOpen(true);
                        }}
                        onSelectBwho={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setBwhoCompactOpen(true);
                        }}
                        onSelectCompany={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setCompanyCompactOpen(true);
                        }}
                        onSelectOilGas={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setOilGasCompactOpen(true);
                        }}
                        onSelectBHPIndonesia={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setBHPIndonesiaCompactOpen(true);
                        }}
                        onSelectProMedico={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setProMedicoCompactOpen(true);
                        }}
                        onSelectCignaCareConnect={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setCignaCareConnectCompactOpen(true);
                        }}
                        onSelectIhhp={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setIhhpCompactOpen(true);
                        }}
                        onSelectMorganPrice={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setMorganPriceCompactOpen(true);
                        }}
                        onSelectOptimumGlobal={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setOptimumGlobalCompactOpen(true);
                        }}
                        onSelectVumi={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setVumiCompactOpen(true);
                        }}
                        onSelectMyHealthPhilippines={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setMyHealthPhilippinesCompactOpen(true);
                        }}
                        onSelectMyHealthSingapore={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setMyHealthSingaporeCompactOpen(true);
                        }}
                        onSelectMyHealthIndonesia={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setMyHealthIndonesiaCompactOpen(true);
                        }}
                        onSelectCignaGlobalHealthOption={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setCignaGlobalHealthOptionCompactOpen(true);
                        }}
                        onSelectPacificCrossPrimaryCarePlan={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setPacificCrossPrimaryCarePlanCompactOpen(true);
                        }}
                        onSelectPacificCrossSelect={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setPacificCrossSelectCompactOpen(true);
                        }}
                        onSelectPacificCrossBlueRoyale={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setPacificCrossBlueRoyaleCompactOpen(true);
                        }}
                        onSelectHSBCSmartCareOptimumEnhanced={() => {
                            refInputTable.current?.getClientData();
                            refInputTable.current?.validateForm();
                            setHSBCSmartCareOptimumEnhancedCompactOpen(true);
                        }}
                    />

                    <ManualPremiumFormDialog
                        open={manualPremiumOpen}
                        onClose={() => { setManualPremiumOpen(false); setEditingPlan(null); }}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleLifelineAddToQuotations}
                        editingPlan={editingPlan}
                    />

                    <Lifeline_Compact_Rates_Dialog
                        open={lifelineCompactOpen}
                        onClose={() => { setLifelineCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleLifelineAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <BWHO_Compact_Rates_Dialog
                        open={bwhoCompactOpen}
                        onClose={() => { setBwhoCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleBwhoAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <Company_Compact_Rates_Dialog
                        open={companyCompactOpen}
                        onClose={() => { setCompanyCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleCompanyAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <OilGas_Compact_Rates_Dialog
                        open={oilGasCompactOpen}
                        onClose={() => { setOilGasCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleOilGasAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <BHP_Indonesia_Compact_Rates_Dialog
                        open={bhpIndonesiaCompactOpen}
                        onClose={() => { setBHPIndonesiaCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleBHPIndonesiaAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <ProMedico_Compact_Rates_Dialog
                        open={proMedicoCompactOpen}
                        onClose={() => { setProMedicoCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleProMedicoAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <CignaCareConnect_Compact_Rates_Dialog
                        open={cignaCareConnectCompactOpen}
                        onClose={() => { setCignaCareConnectCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleCignaCareConnectAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <IHHP_Compact_Rates_Dialog
                        open={ihhpCompactOpen}
                        onClose={() => { setIhhpCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleIhhpAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <MorganPrice_Compact_Rates_Dialog
                        open={morganPriceCompactOpen}
                        onClose={() => { setMorganPriceCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleMorganPriceAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <OptimumGlobal_Compact_Rates_Dialog
                        open={optimumGlobalCompactOpen}
                        onClose={() => { setOptimumGlobalCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleOptimumGlobalAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <Vumi_Compact_Rates_Dialog
                        open={vumiCompactOpen}
                        onClose={() => { setVumiCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleVumiAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <MyHealthPhilippines_Compact_Rates_Dialog
                        open={myHealthPhilippinesCompactOpen}
                        onClose={() => { setMyHealthPhilippinesCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleMyHealthPhilippinesAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <MyHealthSingapore_Compact_Rates_Dialog
                        open={myHealthSingaporeCompactOpen}
                        onClose={() => { setMyHealthSingaporeCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleMyHealthSingaporeAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <MyHealthIndonesia_Compact_Rates_Dialog
                        open={myHealthIndonesiaCompactOpen}
                        onClose={() => { setMyHealthIndonesiaCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleMyHealthIndonesiaAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <CignaGlobalHealthOption_Compact_Rates_Dialog
                        open={cignaGlobalHealthOptionCompactOpen}
                        onClose={() => { setCignaGlobalHealthOptionCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleCignaGlobalHealthOptionAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <PacificCrossPrimaryCarePlan_Compact_Rates_Dialog
                        open={pacificCrossPrimaryCarePlanCompactOpen}
                        onClose={() => { setPacificCrossPrimaryCarePlanCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handlePacificCrossPrimaryCarePlanAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <PacificCrossSelect_Compact_Rates_Dialog
                        open={pacificCrossSelectCompactOpen}
                        onClose={() => { setPacificCrossSelectCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handlePacificCrossSelectAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <PacificCrossBlueRoyale_Compact_Rates_Dialog
                        open={pacificCrossBlueRoyaleCompactOpen}
                        onClose={() => { setPacificCrossBlueRoyaleCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handlePacificCrossBlueRoyaleAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <HSBC_SmartCareOptimumEnhanced_Compact_Rates_Dialog
                        open={hsbcSmartCareOptimumEnhancedCompactOpen}
                        onClose={() => { setHSBCSmartCareOptimumEnhancedCompactOpen(false); setEditingPlan(null); }}
                        appConfig={appConfig}
                        quotes={quotes}
                        clients={clients}
                        quotations={quotations}
                        onAddToQuotations={handleHSBCSmartCareOptimumEnhancedAddToQuotations}
                        editingPlan={editingPlan}
                        isStale={isEditingPlanStale}
                        insurerRates={insurerRates}
                        selectedRatesVersion={selectedRatesVersion}
                        setSelectedRatesVersion={setSelectedRatesVersion}
                        handleFetchAppConfig={handleFetchAppConfig}
                        onRatesVersionChanged={handleRatesVersionChanged}
                    />

                    <AddTobDialog
                        open={addTobOpen}
                        onClose={handleCloseAddTobDialog}
                        onSubmit={handleSubmitAddTob}
                        existingPlanIds={tobPlans.map(p => p.id)}
                        token={auth.token}
                        onAddCustomTob={handleOpenAddCustomTobPlan}
                        customTobFormOpen={addCustomTobPlanOpen}
                    />

                    <CustomTobPlanFormDialog
                        open={addCustomTobPlanOpen}
                        onClose={handleCloseAddCustomTobPlan}
                        tobBenefitLabelsColumn={tobBenefitLabelsColumn}
                        visibleRowNumbers={visibleTobRowNumbers}
                        loadingTobBenefits={loadingTobBenefits}
                        token={auth.token}
                    />

                    <AlertDialog
                        open={deleteTobPlanOpen}
                        setOpen={setDeleteTobPlanOpen}
                        title="Remove TOB"
                        content={tobPlanToDelete ? `Please confirm removing the TOB plan <strong>${tobPlanToDelete.label}</strong>.` : 'Please confirm removing this TOB plan.'}
                        positiveButtonLabel="Remove"
                        negativeButtonLabel="Cancel"
                        callback={confirmDeleteTobPlan}
                    />

                    <AlertDialog
                        open={deletePlanOpen}
                        setOpen={setDeletePlanOpen}
                        title="Remove Plan"
                        content={planToDelete ? `Please confirm removing the plan <strong>${planToDelete.label}</strong>.` : 'Please confirm removing this plan.'}
                        positiveButtonLabel="Remove"
                        negativeButtonLabel="Cancel"
                        callback={confirmDeletePlan}
                    />

                    <AlertDialog
                        open={showRemoveClient}
                        setOpen={setShowRemoveClient}
                        title="Remove Client"
                        content="Are you sure you want to remove this client?"
                        positiveButtonLabel="Remove"
                        negativeButtonLabel="Cancel"
                        callback={() => {
                            handleRemoveClient(removeClientIndex);
                            setShowRemoveClient(false);
                        }}
                    />

                    <AlertDialog
                        open={Boolean(tobLanguageToConfirm)}
                        setOpen={open => { if (!open) setTobLanguageToConfirm(null); }}
                        title="Change Language"
                        content="Any edits made to the TOB cells will be lost."
                        positiveButtonLabel="Change"
                        negativeButtonLabel="Cancel"
                        callback={() => {
                            const language = tobLanguageToConfirm;
                            setTobLanguageToConfirm(null);
                            changeTobLanguage(language);
                        }}
                    />

                    <AlertDialog
                        open={downloadPdfConfirmOpen}
                        setOpen={setDownloadPdfConfirmOpen}
                        title="Download PDF Quotation"
                        content="Are you sure you want to download the PDF quotation?"
                        positiveButtonLabel="Download"
                        negativeButtonLabel="Cancel"
                        callback={handleDownloadPdf}
                    />

                    <AlertDialog
                        open={downloadExcelConfirmOpen}
                        setOpen={setDownloadExcelConfirmOpen}
                        title="Download Excel Quotation"
                        content="Are you sure you want to download the Excel quotation?"
                        positiveButtonLabel="Download"
                        negativeButtonLabel="Cancel"
                        callback={handleDownloadExcel}
                    />

                    {/* Blocks the page while the TOB reloads in the new language. */}
                    <Backdrop open={changingTobLanguage} sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
                        <CircularProgress color="inherit" />
                        <Typography>Loading...</Typography>
                    </Backdrop>

                    <EmailFormDialog
                        key={emailDialogKey}
                        open={emailDialogOpen}
                        onClose={() => setEmailDialogOpen(false)}
                        onSubmit={handleCreateEmailDraft}
                        loading={creatingEmailDraft}
                        clientName={clients[0]?.full_name}
                        quotations={quotations}
                        tobPlans={tobPlans}
                        initialValues={selectedQuotation ? {
                            from: selectedQuotation.email_from,
                            to: selectedQuotation.email_to,
                            cc: selectedQuotation.email_cc,
                            subject: selectedQuotation.email_subject,
                            body: selectedQuotation.email_body,
                        } : null}
                    />
                </Container>
            </Main>
        </Box>
    );
}

export default AutoQuotation;
